import PropTypes from 'prop-types';
import React from 'react';

import PageDetailFrame from './PageDetails/PageDetailFrame';
import TextPageDetail from './PageDetails/TextPageDetail';
import ImagePageDetail from './PageDetails/ImagePageDetail';
import VideoPageDetail from './PageDetails/VideoPageDetail';

import { Button, Plus } from '@opendate-app/setlist';

import DetailImageUpload from '../../../shared/DetailImageUpload';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { WEBSITE_RATIO_LABELS, getAspectRatioValue } from '../../../shared/websiteImageRatios';

import {
  TICKETS_MENU_ITEM
} from '../constants/confirmTicketingConstants';

const Details = ({
  csrfToken,
  team,
  currentUser,
  confirm,
  activeMenuItemChanged,
  pageDetails,
  createPageDetail,
  deletePageDetail,
  pageDetailChanged,
  autoSavePageDetail,
  pageDetailDragged,
  imageCalendarEventAttachments,
  uploadPageDetailImage,
  uploadPageDetailImageUrl,
  uploadConfirmMainImageFile,
  uploadConfirmMainImageUrl,
  uploadConfirmWebsiteImageFile,
  uploadConfirmWebsiteImageUrl,
  mainEventImageLoading,
  websiteEventImageLoading,
  rootUrl,
  artistBios,
  artistBiosChanged,
  pageDetailTemplates,
  deletePageDetailTemplate,
  pageDetailTemplate,
  pageDetailTemplateChanged,
  createPageDetailTemplate
}) => (
  <div className="row">
    <div className="col-12">
      <h3 style={{"fontSize": "20px"}}>
        <strong>
          Main Event Image
        </strong>
      </h3>
      <p className="mb-4">
        This is the first image attendees will see at the top of your listing.<br />
        Use a high quality image with a {WEBSITE_RATIO_LABELS.LANDSCAPE_2_1} aspect ratio.<br />
        Images may not exceed 25MB.
      </p>
      <form onSubmit={
              (e) => {
                e.preventDefault();
              }
            }>
        <div className="form-row mb-3 pt-2">
          <div className="col-12">
            <DetailImageUpload
              imageUrl={confirm.has_main_event_image ? confirm.compressed_main_event_image_url : null}
              sourceImageUrl={confirm.main_event_image_source_url}
              initialCrop={confirm.main_event_image_crop}
              onUploadFile={(file, sourceFile, crop) => uploadConfirmMainImageFile(csrfToken, team, confirm, file, sourceFile, crop)}
              onClearImage={() => uploadConfirmMainImageUrl(csrfToken, team, confirm, "")}
              attachments={imageCalendarEventAttachments}
              onSelectAttachmentUrl={(url) => uploadConfirmMainImageUrl(csrfToken, team, confirm, url)}
              loading={mainEventImageLoading}
            />
          </div>
        </div>
        {confirm.venue_ownership && confirm.venue_ownership.website_image_ratio && confirm.venue_ownership.website_image_ratio !== 'LANDSCAPE_2_1' ? (
          <div className="form-row mb-3 pt-2">
            <div className="col-12">
              <h3 style={{"fontSize": "20px"}}>
                <strong>
                  Website Event Image
                </strong>
              </h3>
              <p>
                This image will display on your website. Choose a high-quality image in the resolution you specified in Settings.<br />
                Recommended aspect ratio: {WEBSITE_RATIO_LABELS[confirm.venue_ownership.website_image_ratio]}.<br />
                Images may not exceed 25MB.
              </p>
              <DetailImageUpload
                imageUrl={confirm.compressed_website_event_image_url || null}
                sourceImageUrl={confirm.website_event_image_source_url}
                initialCrop={confirm.website_event_image_crop}
                onUploadFile={(file, sourceFile, crop) => uploadConfirmWebsiteImageFile(csrfToken, team, confirm, file, sourceFile, crop)}
                onClearImage={() => uploadConfirmWebsiteImageUrl(csrfToken, team, confirm, "")}
                attachments={imageCalendarEventAttachments}
                onSelectAttachmentUrl={(url) => uploadConfirmWebsiteImageUrl(csrfToken, team, confirm, url)}
                loading={websiteEventImageLoading}
                aspectRatio={getAspectRatioValue(confirm.venue_ownership.website_image_ratio)}
              />
            </div>
          </div>
        ) : null}
        <div className="form-row mb-3">
          <div className="col-12">
            <h3 style={{"fontSize": "20px"}}>
              <strong>
                Description
              </strong>
            </h3>
            <p>
              Add more details to your event like your schedule, sponsors, additional photos or featured guests.
            </p>
          </div>
        </div>
        <DragDropContext onDragEnd={
                           (result) => {
                             if (!result.destination) {
                               return false;
                             }

                             var itemIndex = result.source.index;
                             var moveIndex = result.destination.index;
                             var updated = Object.assign({}, pageDetails[itemIndex], {
                               position: (moveIndex + 1)
                             });

                             autoSavePageDetail(csrfToken, team, updated);
                             pageDetailDragged(result);
                           }
                         }>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {pageDetails.sort((a, b) => a.position - b.position).map((pageDetail, index) =>
                  <Draggable key={"" + pageDetail.id} draggableId={"" + pageDetail.id} index={index}>
                    {(provided, snapshot) => (
                      <div ref={provided.innerRef} {...provided.draggableProps}>
                        <PageDetailFrame csrfToken={csrfToken}
                                         confirm={confirm}
                                         pageDetail={pageDetail}
                                         currentUser={currentUser}
                                         deletePageDetail={deletePageDetail}
                                         provided={provided}
                                         team={team}>
                          {pageDetail.type === "PageDetails::Text" ? (
                            <TextPageDetail csrfToken={csrfToken}
                                            key={index}
                                            team={team}
                                            currentUser={currentUser}
                                            confirm={confirm}
                                            pageDetailChanged={pageDetailChanged}
                                            autoSavePageDetail={autoSavePageDetail}
                                            artistBios={artistBios}
                                            artistBiosChanged={artistBiosChanged}
                                            pageDetailTemplates={pageDetailTemplates}
                                            deletePageDetailTemplate={deletePageDetailTemplate}
                                            pageDetailTemplate={pageDetailTemplate}
                                            pageDetailTemplateChanged={pageDetailTemplateChanged}
                                            createPageDetailTemplate={createPageDetailTemplate}
                                            pageDetail={pageDetail} />
                          ) : pageDetail.type === "PageDetails::Image" ? (
                            <ImagePageDetail csrfToken={csrfToken}
                                             key={index}
                                             team={team}
                                             currentUser={currentUser}
                                             confirm={confirm}
                                             imageCalendarEventAttachments={imageCalendarEventAttachments}
                                             uploadPageDetailImage={uploadPageDetailImage}
                                             uploadPageDetailImageUrl={uploadPageDetailImageUrl}
                                             rootUrl={rootUrl}
                                             pageDetail={pageDetail} />
                          ) : pageDetail.type === "PageDetails::Video" ? (
                            <VideoPageDetail csrfToken={csrfToken}
                                             key={index}
                                             team={team}
                                             currentUser={currentUser}
                                             confirm={confirm}
                                             pageDetailChanged={pageDetailChanged}
                                             autoSavePageDetail={autoSavePageDetail}
                                             pageDetail={pageDetail} />
                          ) : null}
                        </PageDetailFrame>
                      </div>
                    )}
                  </Draggable>
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <div className="form-row mt-3">
          <div className="col-12 d-flex" style={{"gap": "8px"}}>
            {[["Text", "PageDetails::Text"], ["Image", "PageDetails::Image"], ["Video", "PageDetails::Video"]].map(([label, type]) => (
              <Button key={type}
                      size="sm"
                      icon={Plus}
                      onClick={() => {
                        var pageDetail = {
                          calendar_event_id: confirm.id,
                          type: type,
                          position: (pageDetails.length + 1)
                        };

                        createPageDetail(csrfToken, team, confirm, pageDetail);
                      }}>
                {label}
              </Button>
            ))}
          </div>
        </div>
      </form>
      <div className="row">
        <div className="col-12 d-flex justify-content-end align-items-end"
             style={{"height": "130px"}}>
          <Button variant="primary"
                  onClick={() => activeMenuItemChanged(TICKETS_MENU_ITEM)}>
            Next
          </Button>
        </div>
      </div>
    </div>
  </div>
);

Details.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  activeMenuItemChanged: PropTypes.func.isRequired,
  pageDetails: PropTypes.array,
  createPageDetail: PropTypes.func.isRequired,
  deletePageDetail: PropTypes.func.isRequired,
  pageDetailChanged: PropTypes.func.isRequired,
  autoSavePageDetail: PropTypes.func.isRequired,
  pageDetailDragged: PropTypes.func.isRequired,
  imageCalendarEventAttachments: PropTypes.array,
  uploadPageDetailImage: PropTypes.func.isRequired,
  uploadPageDetailImageUrl: PropTypes.func.isRequired,
  uploadConfirmMainImageFile: PropTypes.func.isRequired,
  uploadConfirmMainImageUrl: PropTypes.func.isRequired,
  uploadConfirmWebsiteImageFile: PropTypes.func.isRequired,
  uploadConfirmWebsiteImageUrl: PropTypes.func.isRequired,
  websiteEventImageLoading: PropTypes.bool,
  mainEventImageLoading: PropTypes.bool,
  rootUrl: PropTypes.string.isRequired,
  artistBios: PropTypes.object,
  artistBiosChanged: PropTypes.func.isRequired,
  pageDetailTemplates: PropTypes.array,
  deletePageDetailTemplate: PropTypes.func.isRequired,
  pageDetailTemplate: PropTypes.object,
  pageDetailTemplateChanged: PropTypes.func.isRequired,
  createPageDetailTemplate: PropTypes.func.isRequired
};

export default Details;
